import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, IconButton, useTheme, Tooltip, Paper, Chip, Button,
  FormControl, Select, MenuItem, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  PlayArrow as SimulateIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  AutoFixHigh as HealIcon,
  CheckCircleOutline as AcceptIcon,
  BlockOutlined as RejectIcon,
  CheckCircle as OkIcon,
  ErrorOutline as FailedRotIcon,
  RadioButtonUnchecked as SilentIcon,
} from '@mui/icons-material';
import { RootState, AutomationPlaybookType, AutomationPlaybookRunType, AutomationPlaybookPatchProposalType } from '../types';
import { fetchClients } from '../store/slices/clientsSlice';
import { DataTable, Column } from '../components/common';
import { AutomationPlaybooksService } from '../services/automationPlaybooksService';
import { getRelativeTimeString } from '../utils/dateUtils';
import { colorUtils } from '../theme';

const JSON_EXAMPLE = `{
  "name": "Brute-force mitigation",
  "version": "1.0",
  "client_id": "<your-client-id>",
  "description": "Blocks the source IP when a high-reputation malicious IOC is seen.",
  "steps": [
    { "step_key": "start", "type": "start", "on_completion": "gate" },
    {
      "step_key": "gate",
      "type": "if-condition",
      "condition": { "all": { "ioc.reputation_score": { "gte": 75 }, "ioc.is_malicious": true } },
      "on_true": "block_ip",
      "on_false": "end"
    },
    {
      "step_key": "block_ip",
      "type": "action",
      "target": { "vendor": "firewall", "action": "block_ip", "params": { "ip": "{{ioc.ioc_value}}" } },
      "on_completion": "end"
    },
    { "step_key": "end", "type": "end" }
  ]
}`;

const VerdictChip: React.FC<{ verdict: string | null }> = ({ verdict }) => {
  const theme = useTheme();
  if (!verdict) {
    return (
      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>
        Never run
      </Typography>
    );
  }
  const cfg = {
    ok: { label: 'OK', color: theme.palette.success.main, icon: <OkIcon sx={{ fontSize: 12 }} /> },
    silent: { label: 'Silent', color: theme.palette.text.secondary, icon: <SilentIcon sx={{ fontSize: 12 }} /> },
    failed_rot: { label: 'Failed-Rot', color: theme.palette.error.main, icon: <FailedRotIcon sx={{ fontSize: 12 }} /> },
  }[verdict] ?? { label: verdict, color: theme.palette.text.secondary, icon: undefined };
  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      size="small"
      sx={{
        height: 20, fontSize: '0.65rem', fontWeight: 700,
        color: cfg.color,
        backgroundColor: colorUtils.withOpacity(cfg.color, 0.1),
        border: `1px solid ${colorUtils.withOpacity(cfg.color, 0.3)}`,
        '& .MuiChip-icon': { color: cfg.color, ml: 0.75 },
      }}
    />
  );
};

// ── Create dialog — JSON import only for v1, no visual step-graph builder ──

const CreateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (pb: AutomationPlaybookType) => void;
  clients: { id: string; name: string }[];
}> = ({ open, onClose, onCreated, clients }) => {
  const theme = useTheme();
  const [clientId, setClientId] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setClientId(''); setJsonText(''); setError(''); setLoading(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setError('Invalid JSON — fix the syntax before importing');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const created = await AutomationPlaybooksService.create({ ...parsed, client_id: clientId || undefined });
      onCreated(created);
      reset();
    } catch (e: any) {
      setError(e.message ?? 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>New Automation Playbook</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          No visual builder yet — author the step graph as JSON. Leave client empty for a global playbook.
        </Typography>
        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Client (optional)</InputLabel>
          <Select value={clientId} label="Client (optional)" onChange={(e) => setClientId(e.target.value)}>
            <MenuItem value="">Global (all clients)</MenuItem>
            {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          minRows={12}
          maxRows={20}
          placeholder={JSON_EXAMPLE}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          error={!!error}
          helperText={error}
          sx={{ '& textarea': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem' } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" disabled={loading || !jsonText.trim()} onClick={handleCreate}>
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Simulate dialog ──────────────────────────────────────────────────────────

const SimulateDialog: React.FC<{
  playbook: AutomationPlaybookType | null;
  onClose: () => void;
  onRan: (run: AutomationPlaybookRunType) => void;
}> = ({ playbook, onClose, onRan }) => {
  const theme = useTheme();
  const [alertJson, setAlertJson] = useState('{\n  "ioc": {\n    "ioc_value": "203.0.113.42",\n    "reputation_score": 90,\n    "is_malicious": true\n  }\n}');
  const [failingSteps, setFailingSteps] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AutomationPlaybookRunType | null>(null);

  if (!playbook) return null;

  const handleRun = async () => {
    let alert: any;
    try {
      alert = JSON.parse(alertJson);
    } catch {
      setError('Invalid JSON alert payload');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const stepKeys = failingSteps.split(',').map((s) => s.trim()).filter(Boolean);
      const run = await AutomationPlaybooksService.simulate(playbook.id, alert, stepKeys.length ? stepKeys : undefined);
      setResult(run);
      onRan(run);
    } catch (e: any) {
      setError(e.message ?? 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!playbook} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Simulate — {playbook.name}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1, letterSpacing: '0.05em' }}>
          ALERT PAYLOAD
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={6}
          maxRows={12}
          value={alertJson}
          onChange={(e) => setAlertJson(e.target.value)}
          sx={{ mb: 2, '& textarea': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem' } }}
        />
        <Tooltip title="No real vendor adapter exists yet — list step_keys here to simulate a downstream outage and force Failed-Rot, standing in for a real vendor API break.">
          <TextField
            fullWidth
            size="small"
            label="Simulate failing steps (comma-separated step_keys, optional)"
            value={failingSteps}
            onChange={(e) => setFailingSteps(e.target.value)}
            sx={{ mb: 2 }}
          />
        </Tooltip>
        {error && <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 1 }}>{error}</Typography>}
        {result && (
          <Paper sx={{ p: 2, backgroundColor: theme.palette.background.tertiary }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Verdict:</Typography>
              <VerdictChip verdict={result.verdict} />
            </Box>
            {result.failures.length > 0 && (
              <Box>
                {result.failures.map((f, i) => (
                  <Typography key={i} variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, fontFamily: 'monospace', fontSize: '0.72rem' }}>
                    {f.step_key}: {f.error}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={loading} onClick={handleRun} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SimulateIcon sx={{ fontSize: 16 }} />}>
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const PlaybooksPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { clients } = useSelector((state: RootState) => state.clients);

  const [playbooks, setPlaybooks] = useState<AutomationPlaybookType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [simulateTarget, setSimulateTarget] = useState<AutomationPlaybookType | null>(null);

  const [runs, setRuns] = useState<AutomationPlaybookRunType[]>([]);
  const [proposals, setProposals] = useState<AutomationPlaybookPatchProposalType[]>([]);
  const [healingRunId, setHealingRunId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    AutomationPlaybooksService.list(clientFilter || undefined)
      .then((res) => setPlaybooks(res.playbooks))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    AutomationPlaybooksService.listRuns()
      .then((res) => setRuns(res.runs))
      .catch(() => setRuns([]));

    AutomationPlaybooksService.listPatchProposals({ status: 'proposed' })
      .then((res) => setProposals(res.proposals))
      .catch(() => setProposals([]));
  };

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  useEffect(() => { load(); }, [clientFilter]);

  const handleDelete = async (pb: AutomationPlaybookType) => {
    await AutomationPlaybooksService.remove(pb.id);
    load();
  };

  const handleGenerateProposals = async (runId: string) => {
    setHealingRunId(runId);
    try {
      await AutomationPlaybooksService.generatePatchProposals(runId);
      const res = await AutomationPlaybooksService.listPatchProposals({ status: 'proposed' });
      setProposals(res.proposals);
    } finally {
      setHealingRunId(null);
    }
  };

  const handleReview = async (proposal: AutomationPlaybookPatchProposalType, accept: boolean) => {
    if (accept) await AutomationPlaybooksService.acceptPatchProposal(proposal.id);
    else await AutomationPlaybooksService.rejectPatchProposal(proposal.id);
    setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
  };

  const playbookColumns: Column<AutomationPlaybookType>[] = [
    {
      key: 'name', header: 'Name', minWidth: 200, align: 'left',
      render: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.name}</Typography>
          {row.description && (
            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>{row.description}</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'client_id', header: 'Scope', minWidth: 100, align: 'left',
      render: (row) => row.client_id ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {clients.find((c) => c.id === row.client_id)?.name ?? row.client_id.slice(0, 8) + '…'}
        </Typography>
      ) : (
        <Chip label="Global" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
      ),
    },
    { key: 'steps_count', header: 'Steps', minWidth: 60, align: 'left' },
    {
      key: 'last_run_verdict', header: 'Last Run', minWidth: 120, align: 'left',
      render: (row) => <VerdictChip verdict={row.last_run_verdict} />,
    },
    {
      key: 'actions', header: '', minWidth: 100, align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Simulate against an alert">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSimulateTarget(row); }}>
              <SimulateIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const runColumns: Column<AutomationPlaybookRunType>[] = [
    {
      key: 'playbook_id', header: 'Playbook', minWidth: 160, align: 'left',
      render: (row) => (
        <Typography variant="body2">{playbooks.find((p) => p.id === row.playbook_id)?.name ?? row.playbook_id.slice(0, 8) + '…'}</Typography>
      ),
    },
    { key: 'verdict', header: 'Verdict', minWidth: 110, align: 'left', render: (row) => <VerdictChip verdict={row.verdict} /> },
    {
      key: 'created_at', header: 'When', minWidth: 100, align: 'left',
      render: (row) => <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem' }}>{getRelativeTimeString(row.created_at)}</Typography>,
    },
    {
      key: 'actions', header: '', minWidth: 140, align: 'right',
      render: (row) => row.verdict === 'failed_rot' ? (
        <Button
          size="small"
          startIcon={healingRunId === row.id ? <CircularProgress size={12} color="inherit" /> : <HealIcon sx={{ fontSize: 14 }} />}
          disabled={healingRunId === row.id}
          onClick={(e) => { e.stopPropagation(); handleGenerateProposals(row.id); }}
        >
          Propose fix
        </Button>
      ) : null,
    },
  ];

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 10, pb: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 }, mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Automation Playbooks
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Background rule-driven automation — simulate against alerts to catch drift before it reaches production
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={load} sx={{ bgcolor: theme.palette.background.tertiary, borderRadius: 1 }}>
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New Playbook
            </Button>
          </Box>
        </Box>

        <FormControl size="small" sx={{ mb: 3, minWidth: 220 }}>
          <InputLabel sx={{ fontSize: '0.82rem' }}>Client</InputLabel>
          <Select value={clientFilter} label="Client" onChange={(e) => setClientFilter(e.target.value)} displayEmpty>
            <MenuItem value="">All (global + every client)</MenuItem>
            {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ mb: 4 }}>
          <DataTable<AutomationPlaybookType>
            columns={playbookColumns}
            data={playbooks}
            rowKey={(row) => row.id}
            isLoading={loading}
            error={error}
            emptyMessage="No automation playbooks yet"
            emptySubMessage="Create one via JSON import to start simulating it against alerts"
            loadingMessage="Loading automation playbooks…"
            onRetry={load}
            minTableWidth={400}
            rowHeight={56}
          />
        </Box>

        {proposals.length > 0 && (
          <Paper sx={{ p: 2.5, mb: 3, backgroundColor: theme.palette.background.secondary }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Patch Proposals — awaiting review
            </Typography>
            {proposals.map((p) => (
              <Box key={p.id} sx={{ py: 1.5, borderBottom: `1px solid ${theme.palette.border?.main ?? theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.step_key}</Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{p.reasoning}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.72rem', color: theme.palette.text.tertiary, mt: 0.5 }}>
                      {JSON.stringify(p.proposed_target)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Chip label={`${Math.round(p.confidence * 100)}%`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    <Tooltip title="Accept — writes this onto the step">
                      <IconButton size="small" color="success" onClick={() => handleReview(p, true)}><AcceptIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton size="small" color="error" onClick={() => handleReview(p, false)}><RejectIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em', display: 'block', mb: 1.5 }}>
          RUN HISTORY
        </Typography>
        <DataTable<AutomationPlaybookRunType>
          columns={runColumns}
          data={runs}
          rowKey={(row) => row.id}
          isLoading={loading}
          error={null}
          emptyMessage="No simulation runs yet"
          emptySubMessage="Run a simulation from the playbook list above"
          loadingMessage="Loading runs…"
          minTableWidth={400}
          rowHeight={48}
        />
      </Box>

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} clients={clients} />
      <SimulateDialog playbook={simulateTarget} onClose={() => setSimulateTarget(null)} onRan={() => load()} />
    </Box>
  );
};

export default PlaybooksPage;
